import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, Building2, Trash2, Power } from "lucide-react";

const EMPTY = { name: "", code: "", contact_email: "", contact_phone: "", shipping_cost: 9.99 };

export default function AdminCenters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/fulfillment-centers"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/admin/fulfillment-centers", { ...form, shipping_cost: Number(form.shipping_cost) }); toast.success("Center added"); setCreating(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete center "${name}"?`)) return;
    try { await api.delete(`/admin/fulfillment-centers/${id}`); toast.success("Center deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (id, current) => {
    const next = !current;
    try { await api.patch(`/admin/fulfillment-centers/${id}`, { is_active: next }); toast.success(next ? "Center activated" : "Center deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-centers">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Network</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Fulfillment Centers</h1>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover text-white font-manrope text-sm font-semibold transition-colors" data-testid="centers-add">
          <Plus className="w-4 h-4" /> Add Center
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-12 text-center font-manrope text-paw-text-muted dark:text-white/40">No fulfillment centers configured.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <div key={c.center_id} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5 relative" data-testid={`center-${c.center_id}`}>
              <button onClick={() => remove(c.center_id, c.name)} className="absolute top-3 right-3 p-1.5 hover:bg-red-500/10 rounded-md transition-colors" title="Delete center" data-testid={`center-delete-${c.center_id}`}>
                <Trash2 className="w-4 h-4 text-red-700 dark:text-red-400" />
              </button>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-paw-accent flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-outfit font-semibold text-sm">{c.name}</p>
                    {c.is_active !== false
                      ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Active</span>
                      : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-black/[0.06] dark:bg-white/10 text-paw-text-muted dark:text-white/50">Inactive</span>}
                  </div>
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">{c.code}</p>
                </div>
              </div>
              <div className="space-y-1 font-manrope text-xs text-paw-text-secondary dark:text-white/60">
                <p>{c.contact_email || "n/a"}</p>
                <p>{c.contact_phone || "n/a"}</p>
                <p className="text-paw-accent font-medium pt-1">Shipping ${c.shipping_cost ?? "n/a"}</p>
              </div>
              <button onClick={() => toggleActive(c.center_id, c.is_active !== false)} className="mt-4 w-full h-9 rounded-lg border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 text-xs font-manrope font-medium flex items-center justify-center gap-2 transition-colors" data-testid={`center-toggle-${c.center_id}`}>
                <Power className="w-3.5 h-3.5" /> {c.is_active !== false ? "Deactivate" : "Activate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="center-form">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-outfit font-bold text-xl">New Fulfillment Center</h3>
              <button type="button" onClick={() => setCreating(false)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[
                { k: "name", l: "Center Name", req: true },
                { k: "code", l: "Code (e.g., TOR-01)", req: true },
                { k: "contact_email", l: "Contact Email" },
                { k: "contact_phone", l: "Contact Phone" },
                { k: "shipping_cost", l: "Shipping Cost ($)", t: "number", step: "0.01" },
              ].map(({ k, l, req, t, step }) => (
                <div key={k} className="space-y-1">
                  <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">{l}</label>
                  <input type={t || "text"} step={step} required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid={`center-input-${k}`} />
                </div>
              ))}
            </div>
            <button type="submit" disabled={saving} className="w-full mt-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="center-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Center"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
